import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { CategoryIcon, ICON_NAMES } from "../components/CategoryIcon";
import { Modal, Field, inputCls, thCls, tdCls } from "./ui";

const EMPTY = { label: "", slug: "", icon: "Store", enabled: true, order: 99, description: "" };

export default function Categories() {
  const [cats, setCats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/categories").then((r) => setCats(r.data || [])).catch(() => setCats([]));
  useEffect(() => { load(); }, []);

  const toggle = async (c) => {
    await api.put(`/admin/categories/${c.id}`, { ...c, enabled: !c.enabled }).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.label}"? Listings in it will remain but lose their category page.`)) return;
    await api.delete(`/admin/categories/${c.id}`).catch((e) => toast.error(formatApiError(e)));
    toast.success("Category deleted.");
    load();
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/admin/categories", form);
      else await api.put(`/admin/categories/${editing}`, form);
      toast.success("Category saved.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-stone2">Enable, rename, reorder or add categories. Disabled categories disappear from the public site.</p>
        <button onClick={() => { setForm(EMPTY); setEditing("new"); }} className="inline-flex items-center gap-2 bg-ruby text-cream text-sm font-semibold px-5 py-2.5 rounded-xl" data-testid="new-category-btn">
          <Plus className="w-4 h-4" /> Add category
        </button>
      </div>
      <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm overflow-x-auto">
        <table className="w-full text-left min-w-[640px]" data-testid="categories-table">
          <thead className="border-b border-sand">
            <tr><th className={thCls}>Order</th><th className={thCls}>Category</th><th className={thCls}>Slug</th><th className={thCls}>Listings</th><th className={thCls}>Enabled</th><th className={thCls}>Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-sand">
            {(cats || []).map((c) => (
              <tr key={c.id} data-testid={`category-row-${c.slug}`}>
                <td className={tdCls}>{c.order}</td>
                <td className={tdCls}>
                  <span className="flex items-center gap-2.5 font-semibold">
                    <span className="w-8 h-8 rounded-lg bg-gold-light border border-gold/30 flex items-center justify-center text-gold-dark"><CategoryIcon name={c.icon} className="w-4 h-4" /></span>
                    {c.label}
                  </span>
                </td>
                <td className={`${tdCls} font-mono text-xs`}>{c.slug}</td>
                <td className={tdCls}>{c.count}</td>
                <td className={tdCls}>
                  <button
                    onClick={() => toggle(c)}
                    data-testid={`toggle-category-${c.slug}`}
                    className={`w-11 h-6 rounded-full relative transition-colors ${c.enabled ? "bg-jade" : "bg-sand border border-[#E6DED3]"}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${c.enabled ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </td>
                <td className={tdCls}>
                  <div className="flex gap-1">
                    <button onClick={() => { setForm({ ...c }); setEditing(c.id); }} className="p-2 text-clay hover:bg-sand rounded-lg" data-testid={`edit-category-${c.slug}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(c)} className="p-2 text-ruby hover:bg-ruby-light rounded-lg" data-testid={`delete-category-${c.slug}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing === "new" ? "Add category" : "Edit category"} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="space-y-4" data-testid="category-form">
            <Field label="Label *"><input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className={inputCls} data-testid="category-label-input" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Slug (auto if blank)"><input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={inputCls} data-testid="category-slug-input" /></Field>
              <Field label="Order"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className={inputCls} data-testid="category-order-input" /></Field>
            </div>
            <Field label="Icon">
              <select value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className={inputCls} data-testid="category-icon-select">
                {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4 accent-[#8B0018]" data-testid="category-enabled-check" />
              Enabled on public site
            </label>
            <button type="submit" className="w-full bg-ruby text-cream font-bold py-3 rounded-xl" data-testid="category-save-btn">Save category</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
